import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import isEqual from "lodash.isequal";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  useToggleLocalMute,
  useLogger,
  useMeetingManager,
  useVoiceFocus,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import { Mic, MicOff, AudioLines } from "lucide-react";
import { DropdownButtonItem } from "@themes/pollinator/ui/DropdownButtonItem";
import { ButtonDropdownCombo } from "@themes/pollinator/ui/ButtonDropdownCombo";
import { Type } from "@themes/pollinator/ui/Type";

import { isOptionActive } from "@platform/features/video/utils/device-utils";
import { useMemoCompare } from "@core/features/video";
import { ContainerBreakpoints } from "../../videoMeeting.types";
import { videoEventBus } from "@core/features/video";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";
import { usePlatformStore } from "@platform/store";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";

export type Props = {
  muteLabel?: string;
  unmuteLabel?: string;
  containerBreakpoints: ContainerBreakpoints;
};

export const AudioInputVFControl: React.FC<React.PropsWithChildren<Props>> = ({
  muteLabel = "Mute",
  unmuteLabel = "Unmute",
  containerBreakpoints,
}) => {
  const logger = useLogger();
  const meetingManager = useMeetingManager();
  const { addVoiceFocus, isVoiceFocusSupported } = useVoiceFocus();

  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState<ReactNode[] | null>(
    null
  );

  // Reset the ref inside the effect so StrictMode's mount → cleanup → mount
  // cycle in dev builds doesn't leave it permanently false. Without this,
  // every `if (isMountedRef.current) setIsLoading(false)` downstream is
  // silently skipped and isLoading latches forever on the surviving
  // instance.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { muted, toggleMute } = useToggleLocalMute();

  const isNoiseReductionEnabled = usePlatformStore(
    (s) => s.isNoiseReductionEnabled
  );
  const setNoiseReductionEnabled = usePlatformStore(
    (s) => s.setNoiseReductionEnabled
  );
  const selectedAudioInputDeviceId = usePlatformStore(
    (s) => s.selectedAudioInputDevice
  );

  const {
    audioInputDevices: devices,
    selectedAudioInputDevice: selectedDevice,
    audioOutputDevices,
    selectedAudioOutputDevice: audioOutputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
  } = useDevices();

  const { supportsAudio, supportsAudioOutput } =
    useBrowserCompatibilityFeatures();

  const audioInputDevices =
    useMemoCompare(
      devices,
      (
        prev: MediaDeviceInfo[] | undefined,
        next: MediaDeviceInfo[] | undefined
      ): boolean => {
        return isEqual(prev, next);
      }
    ) ?? [];

  // Any change to audioInputDevices means a prior pending device operation
  // is moot — the selected device may have disappeared, the enumeration
  // has advanced. Reset isLoading so disabled items become interactive again
  // even if the prior SDK call hung, rejected silently, or its finally
  // clause was swallowed by a re-render.
  useEffect(() => {
    setIsLoading(false);
  }, [audioInputDevices]);

  // Apply Voice Focus on initial mount once the model is loaded and a device
  // is selected. The ref ensures this fires exactly once per session —
  // it resets when selectedAudioInputDeviceId becomes null (e.g. resetVideo).
  const hasAppliedInitialRef = useRef(false);

  useEffect(() => {
    if (!selectedAudioInputDeviceId) {
      hasAppliedInitialRef.current = false;
      return;
    }
    if (hasAppliedInitialRef.current) return;
    if (!isNoiseReductionEnabled || !isVoiceFocusSupported) return;

    hasAppliedInitialRef.current = true;

    (async () => {
      try {
        const vfDevice = await addVoiceFocus(selectedAudioInputDeviceId);
        if (isMountedRef.current) {
          await meetingManager.startAudioInputDevice(vfDevice);
        }
      } catch (e) {
        logger.error(
          `AudioInputVFControl failed to apply initial Voice Focus: ${e}`
        );
      }
    })();
  }, [
    isNoiseReductionEnabled,
    isVoiceFocusSupported,
    selectedAudioInputDeviceId,
    addVoiceFocus,
    meetingManager,
    logger,
  ]);

  const toggleNoiseReduction = useCallback(async () => {
    if (isLoading || !selectedAudioInputDeviceId) return;

    // eslint-disable-next-line no-console
    console.log("[audio-input] toggleNoiseReduction enter", {
      enabled: isNoiseReductionEnabled,
      deviceId: selectedAudioInputDeviceId,
    });
    setIsLoading(true);
    try {
      if (isNoiseReductionEnabled) {
        // Disable: swap to plain device
        await meetingManager.startAudioInputDevice(selectedAudioInputDeviceId);
        setNoiseReductionEnabled(false);
      } else {
        // Enable: wrap device in Voice Focus
        const vfDevice = await addVoiceFocus(selectedAudioInputDeviceId);
        await meetingManager.startAudioInputDevice(vfDevice);
        setNoiseReductionEnabled(true);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("[audio-input] toggleNoiseReduction caught", e);
      logger.error(
        `AudioInputVFControl failed to toggle noise reduction: ${e}`
      );
    } finally {
      // eslint-disable-next-line no-console
      console.log("[audio-input] toggleNoiseReduction finally, isMounted=", isMountedRef.current);
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    isLoading,
    selectedAudioInputDeviceId,
    isNoiseReductionEnabled,
    addVoiceFocus,
    meetingManager,
    setNoiseReductionEnabled,
    logger,
  ]);

  useEffect(() => {
    const handleClickAudioOutputDevice = async (
      deviceId: string
    ): Promise<void> => {
      try {
        if (new DefaultBrowserBehavior().supportsSetSinkId()) {
          await selectAudioOutputDevice(deviceId);
          await meetingManager.startAudioOutputDevice(deviceId);
          const s = usePlatformStore.getState();
          logMeetingEvent({
            event_type: 'device_selected',
            meeting_id: s.meetingId,
            room_id: s.roomId,
            user: s.user ? { id: s.user.id } : null,
            device_type: 'audio_output',
            device_id: deviceId,
            device_label: audioOutputDevices.find(d => d.deviceId === deviceId)?.label ?? deviceId,
          });
        } else {
          logger.error(
            "AudioOutputControl cannot select audio output device because browser does not support setSinkId operation."
          );
        }
      } catch (error) {
        logger.error("AudioOutputControl failed to select audio output device");
      }
    };

    const handleClick = async (deviceId: string): Promise<void> => {
      // eslint-disable-next-line no-console
      console.log("[audio-input] handleClick enter", deviceId);
      setIsLoading(true);
      try {
        await selectAudioInputDevice(deviceId);
        // Start with plain device ID — onDeviceReplacement in
        // ChimeFeatureWrappers will re-wrap in Voice Focus if the current
        // device is already a VoiceFocusTransformDevice.
        await meetingManager.startAudioInputDevice(deviceId);

        videoEventBus.emitEvent("videochat:audio:source:selected", {
          deviceId,
        });
        const s = usePlatformStore.getState();
        logMeetingEvent({
          event_type: 'device_selected',
          meeting_id: s.meetingId,
          room_id: s.roomId,
          user: s.user ? { id: s.user.id } : null,
          device_type: 'audio_input',
          device_id: deviceId,
          device_label: audioInputDevices.find(d => d.deviceId === deviceId)?.label ?? deviceId,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("[audio-input] handleClick caught", error);
        logger.error("AudioInputVFControl failed to select audio input device");
      } finally {
        // eslint-disable-next-line no-console
        console.log("[audio-input] handleClick finally isMounted=", isMountedRef.current);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    const buildDropdownOptions = async (): Promise<void> => {
      const options: ReactNode[] = [];

      const inputItems: ReactNode[] = await Promise.all(
        audioInputDevices.map(async (device) => (
          <DropdownButtonItem
            key={`audio-input-${device.deviceId}`}
            checked={await isOptionActive(selectedDevice, device.deviceId)}
            onClick={async () => await handleClick(device.deviceId)}
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      const outputItems: ReactNode[] = await Promise.all(
        audioOutputDevices.map(async (device) => (
          <DropdownButtonItem
            key={`audio-output-${device.deviceId}`}
            checked={await isOptionActive(audioOutputDevice, device.deviceId)}
            onClick={async () =>
              await handleClickAudioOutputDevice(device.deviceId)
            }
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      if (supportsAudioOutput) {
        options.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
            key="audio-output"
          >
            Audio Output
          </Type>,
          ...outputItems
        );
      }

      if (supportsAudio) {
        options.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
            key="audio-input"
          >
            Audio Input
          </Type>,
          ...inputItems
        );
      }

      if (isVoiceFocusSupported) {
        options.push(
          <DropdownButtonItem
            key="noise-reduction"
            checked={isNoiseReductionEnabled}
            onClick={toggleNoiseReduction}
            disabled={isLoading}
          >
            <span className="flex items-center gap-2">
              <AudioLines className="w-4 h-4" />
              Noise Reduction
            </span>
          </DropdownButtonItem>
        );
      }

      setDropdownOptions(options);
    };

    buildDropdownOptions();
  }, [
    meetingManager,
    meetingManager.startAudioInputDevice,
    audioInputDevices,
    audioOutputDevices,
    isLoading,
    selectedDevice,
    audioOutputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    isVoiceFocusSupported,
    isNoiseReductionEnabled,
    toggleNoiseReduction,
  ]);

  const handleToggleMute = () => {
    if (muted) {
      videoEventBus.emitEvent("videochat:unmuted", {});
    } else {
      videoEventBus.emitEvent("videochat:muted", {});
    }
    toggleMute();
  };

  return (
    <ButtonDropdownCombo
      buttonIcon={muted ? MicOff : Mic}
      buttonLabel={
        containerBreakpoints && containerBreakpoints.isMd
          ? muted
            ? unmuteLabel
            : muteLabel
          : ""
      }
      buttonOnClick={supportsAudio ? handleToggleMute : () => {}}
      dropdownOptions={supportsAudio ? dropdownOptions || [] : []}
      destructive={muted}
      disabled={!supportsAudio}
    />
  );
};

export default AudioInputVFControl;
